import { unwrap, type components } from '@hive/api';
import { Icon, MenuButton, Pill, useErrorText, useLoad, useToast } from '@hive/ui';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useT, type PlatformKey } from '../../i18n';
import { INSIGHT_SEVERITIES, insightTone } from '../../lib/insights';
import { useSession } from '../../lib/session';

type T = ReturnType<typeof useT>;
type Summary = components['schemas']['hive.core.vuln.InsightsController.Summary'];
const sevLabel = (t: T, s?: string) => (s && (INSIGHT_SEVERITIES as readonly string[]).includes(s) ? t(`ins.sev.${s}` as PlatformKey) : s ?? '—');

/**
 * Sino da barra do topo: quantos insights a pessoa ainda não leu e os 3 principais (HU-1112). "Marcar como lido" (um ou
 * todos) tira do sino e do contador só para ela; o insight continua aberto na tela de Insights, com a data da leitura.
 */
export function InsightsButton() {
  const t = useT();
  const s = useSession();
  const navigate = useNavigate();
  const toast = useToast();
  const errText = useErrorText();
  const list = useRef<HTMLDivElement>(null);
  const [busy, setBusy] = useState(false);
  const sum = useLoad(() => (s.can('vulnerabilities:read') ? unwrap(s.api.GET('/api/v1/insights/summary')).catch(() => null) : Promise.resolve(null)), [s.api]);
  if (!s.can('vulnerabilities:read')) return null;
  const open = sum.data?.open ?? 0;
  const top = sum.data?.top ?? [];

  // depois de marcar, o foco vai para o próximo "marcar como lido" (ou para "ver todos"), sem cair no começo da página
  const refocus = () =>
    window.setTimeout(() => list.current?.querySelector<HTMLElement>('.ins-mark, .ins-all-link')?.focus(), 0);
  const apply = async (call: () => Promise<Summary>) => {
    setBusy(true);
    try {
      const next = await call();
      sum.setData(() => next);
      refocus();
    } catch (e) {
      toast(errText(e), { kind: 'crit' });
    } finally {
      setBusy(false);
    }
  };
  const readOne = (fingerprint: string) => apply(() => unwrap(s.api.POST('/api/v1/insights/read', { params: { query: { fingerprint } } })));
  const readAll = () => apply(() => unwrap(s.api.POST('/api/v1/insights/read-all', { body: { fingerprints: sum.data?.unread ?? [] } })));

  return (
    <MenuButton
      className="icon-btn"
      label={open > 0 ? t('ins.button', { count: open }) : t('ins.title')}
      menuLabel={t('ins.title')}
      button={
        <>
          <Icon name="alert" />
          {open > 0 && (
            <span className="badge-count" aria-hidden="true">
              {open > 99 ? '99+' : open}
            </span>
          )}
        </>
      }
    >
      {(close) => (
        <div ref={list} className="stack-sm ins-pop" aria-busy={busy}>
          <div className="ins-pop-head">
            <b className="small" aria-live="polite">
              {t('ins.popTitle', { count: open })}
            </b>
            {open > 0 && (
              <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={() => void readAll()}>
                <Icon name="check" size="sm" />
                {t('ins.markAll')}
              </button>
            )}
          </div>
          {top.length === 0 ? (
            <span className="small muted">{(sum.data?.allOpen ?? 0) > 0 ? t('ins.allRead') : t('ins.none')}</span>
          ) : (
            top.map((i) => (
              <div key={i.fingerprint} className="ins-row">
                <button
                  type="button"
                  className="pop-item"
                  style={{ whiteSpace: 'normal', textAlign: 'left' }}
                  onClick={() => {
                    close();
                    navigate(`/insights#${encodeURIComponent(i.fingerprint ?? '')}`);
                  }}
                >
                  <Pill tone={insightTone(i.severity)}>{sevLabel(t, i.severity)}</Pill> <span className="small">{i.sentence}</span>
                </button>
                <button
                  type="button"
                  className="icon-btn ins-mark"
                  disabled={busy}
                  aria-label={t('ins.markOne', { name: i.sentence ?? '' })}
                  title={t('ins.markOne', { name: i.sentence ?? '' })}
                  onClick={() => void readOne(i.fingerprint ?? '')}
                >
                  <Icon name="check" size="sm" />
                </button>
              </div>
            ))
          )}
          <button
            type="button"
            className="pop-item ins-all-link"
            onClick={() => {
              close();
              navigate('/insights');
            }}
          >
            <b>{t('ins.seeAll')}</b>
          </button>
        </div>
      )}
    </MenuButton>
  );
}
